import {
  useVaultAuditLogs,
  useExportAuditLogs,
} from '@/hooks/useVault';
import { AuditAction } from '@/types/vault.types';
import {
  Calendar,
  CheckCircle,
  Download,
  FileText,
  Filter,
  Lock,
  Shield,
  XCircle,
  Eye,
  UserPlus,
  UserX,
  Upload,
  Trash2,
  Key,
  Settings,
  AlertTriangle,
  Globe,
  Mail,
  User,
  FileIcon,
  Hash,
  Clock,
  Bot,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface AuditLogsTabProps {
  vaultId: string;
}

// Labels pour les clés de détails
const detailLabels: Record<string, string> = {
  // Invitations
  invitationId: 'ID Invitation',
  invitedEmail: 'Email invité',
  permissionRole: 'Rôle accordé',
  employeeNumber: 'Matricule',

  // Documents
  documentId: 'ID Document',
  filename: 'Nom du fichier',
  originalFilename: 'Fichier original',
  documentType: 'Type de document',
  mimeType: 'Type MIME',
  size: 'Taille',

  // Permissions
  targetUserId: 'Utilisateur cible',
  role: 'Rôle',

  // Vault
  vaultId: 'ID Coffre',
  vaultName: 'Nom du coffre',

  // Général
  reason: 'Raison',
  message: 'Message',

  // Source et automatisation
  source: 'Source',
  automated: 'Automatisé',
  syncSource: 'Source de sync',
  uploadSource: 'Source d\'upload',
};

// Labels pour les valeurs de source
const sourceValueLabels: Record<string, string> = {
  // Sources d'upload
  manual: 'Upload manuel',
  automation: 'Automation (Robot)',
  sync: 'Synchronisation',
  api: 'API',
  import: 'Import',

  // Sources de synchronisation
  openpaye: 'OpenPaye',
  openpaye_sync: 'Sync OpenPaye',
  manual_sync: 'Sync manuelle',
  scheduled_sync: 'Sync planifiée',

  // Valeurs booléennes
  true: 'Oui',
  false: 'Non',
};

// Labels pour les actions
const actionLabels: Record<string, string> = {
  [AuditAction.VAULT_CREATED]: 'Coffre-fort créé',
  [AuditAction.VAULT_ACTIVATED]: 'Coffre-fort activé',
  [AuditAction.VAULT_INITIALIZED]: 'Coffre-fort initialisé',
  [AuditAction.VAULT_UNLOCKED]: 'Coffre-fort déverrouillé',
  [AuditAction.VAULT_SUSPENDED]: 'Coffre-fort suspendu',
  [AuditAction.VAULT_CLOSED]: 'Coffre-fort fermé',
  [AuditAction.DOCUMENT_UPLOADED]: 'Document ajouté',
  [AuditAction.DOCUMENT_DOWNLOADED]: 'Document téléchargé',
  [AuditAction.DOCUMENT_VIEWED]: 'Document consulté',
  [AuditAction.DOCUMENT_DELETED]: 'Document supprimé',
  [AuditAction.DOCUMENT_SHARED]: 'Document partagé',
  [AuditAction.PERMISSION_GRANTED]: 'Permission accordée',
  [AuditAction.PERMISSION_REVOKED]: 'Permission révoquée',
  [AuditAction.PERMISSION_MODIFIED]: 'Permission modifiée',
  [AuditAction.INVITATION_SENT]: 'Invitation envoyée',
  [AuditAction.INVITATION_ACCEPTED]: 'Invitation acceptée',
  [AuditAction.INVITATION_DECLINED]: 'Invitation refusée',
  [AuditAction.INVITATION_EXPIRED]: 'Invitation expirée',
  [AuditAction.ACCESS_GRANTED]: 'Accès accordé',
  [AuditAction.ACCESS_DENIED]: 'Accès refusé',
  [AuditAction.LOGIN_SUCCESS]: 'Connexion réussie',
  [AuditAction.LOGIN_FAILED]: 'Échec de connexion',
  [AuditAction.EXPORT_REQUESTED]: 'Export demandé',
  [AuditAction.EXPORT_COMPLETED]: 'Export terminé',
};

// Formater les rôles
const roleLabels: Record<string, string> = {
  owner: 'Propriétaire',
  admin: 'Administrateur',
  contributor: 'Contributeur',
  viewer: 'Lecteur',
  OWNER: 'Propriétaire',
  ADMIN: 'Administrateur',
  CONTRIBUTOR: 'Contributeur',
  VIEWER: 'Lecteur',
};

// Formater la taille de fichier
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

// Formater une valeur de détail
const formatDetailValue = (key: string, value: unknown): string => {
  if (value === null || value === undefined) return 'N/A';

  // Rôles
  if (key === 'permissionRole' || key === 'role') {
    return roleLabels[String(value)] || String(value);
  }

  // Sources
  if (key === 'source' || key === 'syncSource' || key === 'uploadSource') {
    return sourceValueLabels[String(value)] || String(value);
  }

  // Booléens (automated, etc.)
  if (key === 'automated' && typeof value === 'boolean') {
    return value ? 'Oui' : 'Non';
  }

  // Taille
  if (key === 'size' && typeof value === 'number') {
    return formatFileSize(value);
  }

  // UUIDs - tronquer pour lisibilité
  if (key.endsWith('Id') && typeof value === 'string' && value.length > 20) {
    return `${value.substring(0, 8)}...`;
  }

  return String(value);
};

export const AuditLogsTab = ({ vaultId }: AuditLogsTabProps) => {
  const { t } = useTranslation();
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const { data: auditLogs, isLoading } = useVaultAuditLogs(vaultId);
  const exportAuditLogs = useExportAuditLogs(vaultId);

  const handleExport = async (format: 'json' | 'csv') => {
    await exportAuditLogs.mutateAsync(format);
  };

  const getActionIcon = (action: AuditAction) => {
    switch (action) {
      case AuditAction.VAULT_CREATED:
        return <Shield className="h-4 w-4 text-blue-600" />;
      case AuditAction.VAULT_ACTIVATED:
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case AuditAction.VAULT_INITIALIZED:
        return <Key className="h-4 w-4 text-purple-600" />;
      case AuditAction.VAULT_UNLOCKED:
        return <Lock className="h-4 w-4 text-green-600" />;
      case AuditAction.DOCUMENT_UPLOADED:
        return <Upload className="h-4 w-4 text-blue-600" />;
      case AuditAction.DOCUMENT_DOWNLOADED:
        return <Download className="h-4 w-4 text-neutral-600" />;
      case AuditAction.DOCUMENT_DELETED:
        return <Trash2 className="h-4 w-4 text-red-600" />;
      case AuditAction.DOCUMENT_VIEWED:
        return <Eye className="h-4 w-4 text-neutral-600" />;
      case AuditAction.PERMISSION_GRANTED:
        return <UserPlus className="h-4 w-4 text-green-600" />;
      case AuditAction.PERMISSION_REVOKED:
        return <UserX className="h-4 w-4 text-red-600" />;
      case AuditAction.INVITATION_SENT:
        return <Mail className="h-4 w-4 text-purple-600" />;
      case AuditAction.INVITATION_ACCEPTED:
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case AuditAction.INVITATION_DECLINED:
        return <XCircle className="h-4 w-4 text-red-600" />;
      case AuditAction.VAULT_SUSPENDED:
        return <AlertTriangle className="h-4 w-4 text-amber-600" />;
      case AuditAction.VAULT_CLOSED:
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Settings className="h-4 w-4 text-neutral-600" />;
    }
  };

  const getActionColor = (action: AuditAction) => {
    switch (action) {
      case AuditAction.VAULT_CREATED:
      case AuditAction.VAULT_ACTIVATED:
      case AuditAction.VAULT_INITIALIZED:
      case AuditAction.DOCUMENT_UPLOADED:
      case AuditAction.PERMISSION_GRANTED:
      case AuditAction.INVITATION_ACCEPTED:
        return 'bg-green-50 border-green-200';
      case AuditAction.DOCUMENT_DELETED:
      case AuditAction.PERMISSION_REVOKED:
      case AuditAction.VAULT_CLOSED:
      case AuditAction.INVITATION_DECLINED:
        return 'bg-red-50 border-red-200';
      case AuditAction.VAULT_SUSPENDED:
        return 'bg-amber-50 border-amber-200';
      case AuditAction.INVITATION_SENT:
        return 'bg-purple-50 border-purple-200';
      default:
        return 'bg-neutral-50 border-neutral-200';
    }
  };

  const getActionLabel = (action: AuditAction): string => {
    return actionLabels[action] || action.replace(/_/g, ' ');
  };

  // Rendre les détails de manière lisible
  const renderDetails = (details: string | Record<string, unknown> | undefined) => {
    if (!details) return null;

    if (typeof details === 'string') {
      return <p className="text-sm text-neutral-600">{details}</p>;
    }

    // Filtrer les clés à afficher (exclure les IDs internes si souhaité)
    const entries = Object.entries(details).filter(
      ([_key, value]) => value !== null && value !== undefined && value !== ''
    );

    if (entries.length === 0) return null;

    return (
      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {entries.map(([key, value]) => (
          <div
            key={key}
            className="flex items-center gap-2 rounded-md bg-neutral-50 px-3 py-2"
          >
            {key === 'invitedEmail' || key === 'email' ? (
              <Mail className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400" />
            ) : key === 'permissionRole' || key === 'role' ? (
              <User className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400" />
            ) : key === 'filename' || key === 'originalFilename' ? (
              <FileIcon className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400" />
            ) : key === 'employeeNumber' ? (
              <Hash className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400" />
            ) : key === 'automated' ? (
              <Bot className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400" />
            ) : key === 'source' || key === 'syncSource' || key === 'uploadSource' ? (
              <Zap className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400" />
            ) : (
              <span className="h-3.5 w-3.5 flex-shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <span className="text-xs font-medium text-neutral-500">
                {detailLabels[key] || key}
              </span>
              <p className="truncate text-sm font-medium text-neutral-800">
                {formatDetailValue(key, value)}
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const logs = auditLogs?.logs || [];
  const filteredLogs = logs.filter((log) => {
    const matchesAction =
      actionFilter === 'all' || log.action === actionFilter;
    const detailsStr = log.details
      ? typeof log.details === 'string'
        ? log.details
        : JSON.stringify(log.details)
      : '';
    const matchesSearch =
      searchTerm === '' ||
      log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      detailsStr.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.ipAddress?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesAction && matchesSearch;
  });

  if (isLoading) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-6">
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-50">
            <Calendar className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">
              {t('vault.audit.title', "Historique d'Audit")}
            </h2>
            <p className="text-sm text-neutral-500">
              {t('vault.audit.description', 'Journal cryptographique des actions')}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => handleExport('json')}
            disabled={exportAuditLogs.isPending}
            className="flex items-center gap-2 rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
          >
            <Download className="h-4 w-4" />
            {t('vault.audit.exportJson', 'JSON')}
          </button>
          <button
            onClick={() => handleExport('csv')}
            disabled={exportAuditLogs.isPending}
            className="flex items-center gap-2 rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
          >
            <FileText className="h-4 w-4" />
            {t('vault.audit.exportCsv', 'CSV')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <div className="flex-1">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('vault.audit.search', 'Rechercher dans les logs...')}
            className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-neutral-500" />
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
          >
            <option value="all">{t('vault.audit.allActions', 'Toutes les actions')}</option>
            <option value={AuditAction.VAULT_CREATED}>Coffre créé</option>
            <option value={AuditAction.VAULT_ACTIVATED}>Coffre activé</option>
            <option value={AuditAction.DOCUMENT_UPLOADED}>Document ajouté</option>
            <option value={AuditAction.DOCUMENT_DOWNLOADED}>Document téléchargé</option>
            <option value={AuditAction.DOCUMENT_DELETED}>Document supprimé</option>
            <option value={AuditAction.PERMISSION_GRANTED}>Permission accordée</option>
            <option value={AuditAction.PERMISSION_REVOKED}>Permission révoquée</option>
            <option value={AuditAction.INVITATION_SENT}>Invitation envoyée</option>
            <option value={AuditAction.INVITATION_ACCEPTED}>Invitation acceptée</option>
          </select>
        </div>
      </div>

      {/* Audit Logs Timeline */}
      {filteredLogs && filteredLogs.length > 0 ? (
        <div className="relative space-y-4">
          {/* Timeline line */}
          <div className="absolute left-6 top-2 bottom-2 w-0.5 bg-neutral-200" />

          {filteredLogs.map((log) => (
            <div key={log.uuid} className="relative flex gap-4">
              {/* Timeline dot */}
              <div className={`relative z-10 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border-2 ${getActionColor(log.action)}`}>
                {getActionIcon(log.action)}
              </div>

              {/* Log content */}
              <div className="flex-1 rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-3">
                  {/* Header */}
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h4 className="text-sm font-semibold text-neutral-900">
                      {getActionLabel(log.action)}
                    </h4>
                    <div className="flex items-center gap-2 text-xs text-neutral-500">
                      <Clock className="h-3.5 w-3.5" />
                      {new Date(log.createdAt).toLocaleString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })}
                    </div>
                  </div>

                  {/* Details */}
                  {renderDetails(log.details)}

                  {/* Footer with IP and Hash */}
                  <div className="flex flex-wrap items-center gap-4 border-t border-neutral-100 pt-3 text-xs">
                    {/* IP Address */}
                    <div className="flex items-center gap-1.5">
                      <Globe className="h-3.5 w-3.5 text-neutral-400" />
                      <span className="font-medium text-neutral-500">IP:</span>
                      <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-neutral-700">
                        {log.ipAddress || 'Non disponible'}
                      </code>
                    </div>

                    {/* Hash */}
                    <div className="flex items-center gap-1.5">
                      <Shield className="h-3.5 w-3.5 text-neutral-400" />
                      <span className="font-medium text-neutral-500">Hash:</span>
                      <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-neutral-700">
                        {log.hash?.substring(0, 16)}...
                      </code>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-neutral-300 bg-neutral-50 p-12 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-neutral-200">
            <Calendar className="h-8 w-8 text-neutral-500" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-neutral-900">
            {t('vault.audit.empty', 'Aucun événement')}
          </h3>
          <p className="text-sm text-neutral-600">
            {searchTerm || actionFilter !== 'all'
              ? t('vault.audit.noMatch', 'Aucun événement ne correspond aux filtres')
              : t('vault.audit.emptyDescription', 'Les actions seront enregistrées ici')}
          </p>
        </div>
      )}
    </div>
  );
};
